// Pagination utilities for V2 API integration

/**
 * Pagination utilities for handling server-side pagination with nextToken
 * Used by usePaginatedProducts hook and related components
 */

/**
 * Generate a cache key for paginated API responses
 * @param {string} endpoint - API endpoint
 * @param {Object} params - Query parameters
 * @param {number} page - Page number (for cache organization)
 * @returns {string} Unique cache key
 */
export const generateCacheKey = (endpoint, params = {}, page = 1) => {
  // Sort parameters for consistent cache keys
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}=${params[key]}`)
    .join('&');
  
  // Remove /api/ prefix and replace slashes with underscores for cleaner keys
  const cleanEndpoint = endpoint.replace(/^\/api\//, '').replace(/\//g, '_');
  
  return `v2_${cleanEndpoint}_${sortedParams}_page_${page}`;
};

/**
 * Serialize pagination state for URL or localStorage persistence
 * @param {Object} state - Pagination state
 * @param {Array} state.tokens - Array of nextTokens for each page
 * @param {number} state.currentPage - Current page number
 * @param {boolean} state.hasMore - Whether more pages are available
 * @returns {string} Serialized state
 */
export const serializePaginationState = (state) => {
  try {
    return btoa(JSON.stringify({
      tokens: state.tokens || [],
      currentPage: state.currentPage || 1,
      hasMore: state.hasMore || false,
      timestamp: Date.now()
    }));
  } catch (error) {
    console.warn('Failed to serialize pagination state:', error);
    return '';
  }
};

/**
 * Deserialize pagination state from URL or localStorage
 * @param {string} serializedState - Serialized pagination state
 * @param {number} maxAge - Maximum age in milliseconds (default: 30 minutes)
 * @returns {Object|null} Deserialized state or null if invalid/expired
 */
export const deserializePaginationState = (serializedState, maxAge = 30 * 60 * 1000) => {
  try {
    if (!serializedState) return null;
    
    const decoded = JSON.parse(atob(serializedState));
    
    // Check if state is expired
    if (decoded.timestamp && (Date.now() - decoded.timestamp) > maxAge) {
      return null;
    }
    
    return {
      tokens: decoded.tokens || [],
      currentPage: decoded.currentPage || 1,
      hasMore: decoded.hasMore || false
    };
  } catch (error) {
    console.warn('Failed to deserialize pagination state:', error);
    return null;
  }
};

/**
 * Normalize V2 API response structure
 * Ensures consistent response format across different V2 endpoints
 * @param {Object} response - Raw API response
 * @returns {Object} Normalized response
 */
export const normalizeV2Response = (response) => {
  const data = response.data || response;
  
  // Handle different response structures
  if (Array.isArray(data)) {
    // Direct array response (legacy format)
    return {
      products: data,
      pagination: {
        hasMore: false,
        nextToken: null,
        total: data.length
      }
    };
  }
  
  // Modern V2 response structure - handle nested data structure
  const products = data.products || data.items || data.data || [];
  const nextToken = data.pagination?.nextToken || data.nextToken || null;
  
  // Enhanced hasMore logic: 
  // 1. True if there's a nextToken
  // 2. True if API explicitly says hasMore or hasNextPage
  // 3. For V2 APIs without proper pagination, estimate based on product count vs limit
  let hasMore = false;
  
  if (nextToken) {
    // Definitely has more if there's a nextToken
    hasMore = true;
  } else if (data.pagination?.hasNextPage || data.pagination?.hasMore || data.hasMore) {
    // API explicitly says there are more (V2 uses hasNextPage, V1 fallback uses hasMore)
    hasMore = true;
  } else if (products.length > 0) {
    // For V2 APIs that might not implement proper pagination,
    // estimate based on whether we got a "full page" of results
    // This is a fallback heuristic
    const expectedLimit = parseInt(process.env.REACT_APP_PAGINATION_LIMIT) || 100; // Use dynamic limit
    const isFullPage = products.length >= expectedLimit * 0.9; // 90% threshold
    
    // Only assume more products if we got close to a full page
    // This prevents showing "load more" when there are clearly no more products
    hasMore = isFullPage && products.length >= Math.min(expectedLimit * 0.5, 10); // More flexible minimum
  }
  
  const total = data.pagination?.total || data.pagination?.totalItems || data.total || null;
  
  // Enhanced debug logging to understand V2 API responses
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_PAGINATION === 'true') {
    console.log('🔍 [V2 API Response] normalizeV2Response:', {
      rawDataStructure: Array.isArray(data) ? 'ARRAY' : 'OBJECT',
      productsLength: products.length,
      hasMore,
      nextToken: nextToken ? '[TOKEN_PRESENT]' : null,
      total,
      rawData: {
        hasPagination: !!data.pagination,
        hasNextToken: !!data.nextToken,
        hasDirectHasMore: !!data.hasMore,
        hasNextPage: !!data.pagination?.hasNextPage,
        paginationNextToken: data.pagination?.nextToken ? '[TOKEN_PRESENT]' : null,
        paginationHasMore: data.pagination?.hasMore,
        paginationHasNextPage: data.pagination?.hasNextPage,
        paginationCount: data.pagination?.count,
        paginationLimit: data.pagination?.limit,
        paginationTotal: data.pagination?.total,
        paginationTotalItems: data.pagination?.totalItems
      }
    });
  }
  
  return {
    products: products,
    pagination: {
      hasMore: hasMore,
      nextToken: nextToken,
      total: total
    }
  };
};

/**
 * Calculate optimal page size based on device and connection
 * @param {Object} options - Configuration options
 * @param {number} options.defaultLimit - Default page size
 * @param {boolean} options.isMobile - Whether user is on mobile device
 * @param {string} options.connectionType - Network connection type
 * @returns {number} Optimal page size
 */
export const calculateOptimalPageSize = ({ 
  defaultLimit = 100, 
  isMobile = false, 
  connectionType = 'unknown' 
}) => {
  // Reduce page size for mobile devices
  if (isMobile) {
    defaultLimit = Math.min(defaultLimit, 50);
  }
  
  // Further reduce for slow connections
  if (['slow-2g', '2g', '3g'].includes(connectionType)) {
    defaultLimit = Math.min(defaultLimit, 25);
  }
  
  return Math.max(defaultLimit, 10); // Minimum of 10 items
};

/**
 * Create pagination metadata object
 * @param {Object} options - Pagination options
 * @returns {Object} Pagination metadata
 */
export const createPaginationMeta = ({
  currentPage = 1,
  totalPages = null,
  hasMore = false,
  nextToken = null,
  itemsPerPage = 100,
  totalItems = null
}) => ({
  currentPage,
  totalPages,
  hasMore,
  nextToken,
  itemsPerPage,
  totalItems,
  isFirstPage: currentPage === 1,
  isLastPage: !hasMore
});

/**
 * Validate pagination parameters before API call
 * @param {Object} params - API parameters
 * @returns {Object} Validated parameters
 */
export const validatePaginationParams = (params) => {
  const validated = { ...params };
  
  // Ensure limit is within acceptable range
  if (validated.limit) {
    validated.limit = Math.min(Math.max(validated.limit, 1), 200);
  }
  
  // Validate nextToken format (basic validation)
  if (validated.nextToken && typeof validated.nextToken !== 'string') {
    console.warn('Invalid nextToken format, removing from params');
    delete validated.nextToken;
  }
  
  return validated;
};

/**
 * Merge paginated data with existing data
 * Handles both append (infinite scroll) and replace (new search) scenarios
 * @param {Array} existingData - Current data array
 * @param {Array} newData - New data to merge
 * @param {string} mode - Merge mode: 'append' or 'replace'
 * @param {string} idField - Field to use for deduplication (default: 'id')
 * @returns {Array} Merged data array
 */
export const mergePaginatedData = (
  existingData = [], 
  newData = [], 
  mode = 'append', 
  idField = 'id'
) => {
  if (mode === 'replace') {
    return newData;
  }
  
  if (mode === 'append') {
    // Deduplicate based on ID field
    const existingIds = new Set(existingData.map(item => item[idField]));
    const uniqueNewData = newData.filter(item => !existingIds.has(item[idField]));
    
    return [...existingData, ...uniqueNewData];
  }
  
  return newData;
};

/**
 * Cache management utilities for paginated data
 */
export const paginationCache = {
  /**
   * Store paginated data in cache with TTL and memory management
   * @param {string} key - Cache key
   * @param {Object} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds
   */
  set: (key, data, ttl = 5 * 60 * 1000) => {
    try {
      // Implement memory management - remove old entries if too many
      const keys = Object.keys(sessionStorage);
      const paginationKeys = keys.filter(k => k.startsWith('v2_'));
      
      // If we have too many cached entries, remove the oldest ones
      if (paginationKeys.length >= 50) { // Limit to 50 cache entries
        const cacheItems = paginationKeys.map(k => {
          try {
            const item = JSON.parse(sessionStorage.getItem(k));
            return { key: k, timestamp: item.timestamp };
          } catch {
            return { key: k, timestamp: 0 };
          }
        }).sort((a, b) => a.timestamp - b.timestamp);
        
        // Remove oldest 25% of entries
        const toRemove = Math.floor(cacheItems.length * 0.25);
        for (let i = 0; i < toRemove; i++) {
          sessionStorage.removeItem(cacheItems[i].key);
        }
      }
      
      const cacheItem = {
        data,
        timestamp: Date.now(),
        ttl
      };
      sessionStorage.setItem(key, JSON.stringify(cacheItem));
    } catch (error) {
      console.warn('Failed to set pagination cache:', error);
      // If storage is full, try clearing old entries and retry once
      try {
        const keys = Object.keys(sessionStorage);
        const paginationKeys = keys.filter(k => k.startsWith('v2_'));
        paginationKeys.forEach(k => sessionStorage.removeItem(k));
        
        const cacheItem = {
          data,
          timestamp: Date.now(),
          ttl
        };
        sessionStorage.setItem(key, JSON.stringify(cacheItem));
      } catch (retryError) {
        console.warn('Failed to set pagination cache after cleanup:', retryError);
      }
    }
  },

  /**
   * Retrieve data from cache if not expired
   * @param {string} key - Cache key
   * @returns {Object|null} Cached data or null if expired/not found
   */
  get: (key) => {
    try {
      const cached = sessionStorage.getItem(key);
      if (!cached) return null;

      const cacheItem = JSON.parse(cached);
      const isExpired = (Date.now() - cacheItem.timestamp) > cacheItem.ttl;
      
      if (isExpired) {
        sessionStorage.removeItem(key);
        return null;
      }

      return cacheItem.data;
    } catch (error) {
      console.warn('Failed to get pagination cache:', error);
      return null;
    }
  },

  /**
   * Remove specific cache entry
   * @param {string} key - Cache key to remove
   */
  remove: (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('Failed to remove pagination cache:', error);
    }
  },

  /**
   * Clear all pagination cache entries
   */
  clear: () => {
    try {
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('v2_')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear pagination cache:', error);
    }
  }
};
